import React, { createContext, useState, useContext, useEffect } from 'react';
import authService from '../services/authService';
import loyaltyService from '../services/loyaltyService';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);  // Set loading is only for the initial load when we check for a token in localStorage
  const [error, setError] = useState(null);
  const [org, setOrg] = useState(null);

  useEffect(() => {
    const loadInitialData = async () => {
      let orgPromise = Promise.resolve();
      
      // Extract subdomain from URL
      const hostname = window.location.hostname;
      const parts = hostname.split('.');
      
      if (parts.length >= 2) {
        const subdomain = parts[0];
        const reservedSubdomains = ['loyalty-portal', 'portal', 'members'];
        
        if (!reservedSubdomains.includes(subdomain)) {
          // Fetch public group info for the subdomain
          orgPromise = loyaltyService.getPublicGroupInfo(subdomain)
            .then(groupData => {
              setOrg(groupData);
            })
            .catch(err => {
              console.error('Failed to fetch group info for subdomain:', subdomain, err);
              // Still set the subdomain even if API call fails
              setOrg(subdomain);
            });
        }
      }

      // Check if user is logged in from localStorage
      const token = localStorage.getItem('token');
      let userPromise = Promise.resolve();
      
      if (token) {
        // Call the mock API service (would be authService in production)
        userPromise = authService.getMe()
          .then(user => {
            setUser(user);
          })
          .catch(err => {
            console.error('Failed to fetch user', err);
            setError(err.message || 'Failed to fetch user');
          });
      }
      
      // Wait for both promises to complete before setting loading to false
      await Promise.all([orgPromise, userPromise]);
      setLoading(false);
    };
    
    loadInitialData();
  }, []);

  const login = async (email, password) => {
    try {
      setError(null);

      // Call the mock API service (would be authService in production)
      const response = await authService.login(email, password);

      // Save user to state and localStorage
      setUser(response.member);
      localStorage.setItem('token', response.token.key);

      return response.user;
    } catch (err) {
      setError(err.message || 'Failed to login');
      throw err;
    }
  };

  const logout = async () => {
    try {
      await authService.logout();
      setUser(null);
    } catch (error) {
      console.error('Error trying to log out:', error);
      throw error;
    }
  };

  const register = async (userData) => {
    try {
      setError(null);

      // Call the auth service to register
      const response = await authService.register({
        first_name: userData.firstName,
        last_name: userData.lastName,
        email: userData.email,
        phone: userData.phone,
        password: userData.password,
        confirm_password: userData.confirmPassword,
        address1: userData.address1 || null,
        city: userData.city || null,
        state: userData.state || null,
        zip_code: userData.zipCode || null,
        birth_month: userData.birthMonth || null,
        birth_day: userData.birthDay || null,
        birth_year: userData.birthYear || null,
        phone_code: userData.phoneCode || undefined
      });

      // The phone belongs to a member enrolled at a store. The API texted them a code and saved nothing yet;
      // the caller repeats the registration with the code to claim that member's account.
      if (response.phone_verification_required) {
        return { phoneVerificationRequired: true, detail: response.detail };
      }

      // Automatically log in after successful registration
      setUser(response.member);
      localStorage.setItem('token', response.token.key);

      return response.member;
    } catch (err) {
      setError(err.message || 'Failed to register');
      throw err;
    }
  };

  const sendVerificationEmail = async () => {
    try {
      setError(null);
      await authService.sendVerificationEmail();
    } catch (err) {
      setError(err.message || 'Failed to send verification code');
      throw err;
    }
  };

  const verifyEmail = async (code) => {
    try {
      setError(null);

      const response = await authService.verifyEmail(code);

      // Update user with verified email status
      if (response.success) {
        const updatedUser = await authService.getMe();
        setUser(updatedUser);
      }

      return response;
    } catch (err) {
      setError(err.message || 'Failed to verify email');
      throw err;
    }
  };

  const sendVerificationSMS = async () => {
    try {
      setError(null);
      await authService.sendVerificationSMS();
    } catch (err) {
      setError(err.message || 'Failed to send SMS verification code');
      throw err;
    }
  };

  const verifyPhone = async (code) => {
    try {
      setError(null);

      const response = await authService.verifyPhone(code);

      // Update user with verified phone status
      if (response.success) {
        const updatedUser = await authService.getMe();
        setUser(updatedUser);
      }

      return response;
    } catch (err) {
      setError(err.message || 'Failed to verify phone');
      throw err;
    }
  };

  const updateUser = (updatedUserData) => {
    setUser(prevUser => ({
      ...prevUser,
      ...updatedUserData
    }));
  };

  return (
    <AuthContext.Provider value={{
      user,
      org,
      login,
      logout,
      register,
      loading,
      error,
      sendVerificationEmail,
      verifyEmail,
      sendVerificationSMS,
      verifyPhone,
      updateUser
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);